<template>
  <div class="water-delivery-form">
    <validation-observer ref="formValidation">
      <!-- Delivery Address Section -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.address')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.deliveryAddress')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <div v-if="hasAddressOptions">
            <b-form-radio-group
              v-model="addressMode"
              :options="addressModeOptions"
              :state="errors.length === 0 ? null : false"
              class="mb-1"
            />

            <n-single-select
              v-if="addressMode === 'rental'"
              v-model="formData.address"
              :options="rentalAddressOptions"
              :selection-label="'text'"
              :selection-key="'value'"
              :reduce="true"
              :clearable="false"
              :placeholder="$t('placeholder.selectAddress')"
            />

            <b-form-textarea
              v-else
              v-model="customAddress"
              :rows="3"
              :state="errors.length === 0 ? null : false"
              :placeholder="$t('placeholder.enterAddress')"
              class="mt-1"
            />
          </div>
          <b-form-textarea
            v-else
            v-model="customAddress"
            :rows="3"
            :state="errors.length === 0 ? null : false"
            :placeholder="$t('placeholder.enterAddress')"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Service Description -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.description')"
        rules=""
      >
        <b-form-group
          :label="$t('field.serviceDescription')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <b-form-textarea
            v-model="formData.description"
            :rows="3"
            :state="errors.length === 0 ? null : false"
            :placeholder="$t('placeholder.enterDescription')"
            maxlength="300"
          />
          <div class="d-flex justify-content-between align-items-center mt-1">
            <small class="text-muted">{{ formData.description.length }}/300</small>
            <small class="text-danger">{{ errors[0] }}</small>
          </div>
        </b-form-group>
      </validation-provider>

      <!-- Product List Section -->
      <div class="product-section">
        <b-form-group :label="$t('service.water_service')">
          <div v-if="loadingProducts">...</div>
          <div v-if="!products || products.length === 0" class="text-muted">
              {{ $t('general.noItems') }}
            </div>
          <div v-else class="product-list">
            <div
              v-for="product in products"
              :key="product.id || product.code"
              class="product-item"
            >
              <div class="product-thumb">
                <img
                  :src="product.photo || '/bottle-of-water.png'"
                  alt=""
                  style="padding: 10px;"
                  @error="onImageError"
                />
              </div>
              <div>
                <div class="product-title">{{ getProductName(product) }}</div>
                <div class="product-price">
                  {{ formatPrice(product.price) }}
                </div>
              </div>
              <div class="qty-control">
                <b-form-input
                  type="number"
                  class="qty-input"
                  :value="getQty(product)"
                  min="0"
                  @input="setQty(product, $event)"
                />
                <!-- <button
                  class="qty-btn primary-color"
                  @click="increment(product)"
                >
                  +
                </button> -->
              </div>
            </div>
            <div v-if="hasMore && !loadingProducts" class="mt-1">
              <button
                class="btn-load-more btn btn-outline-primary btn-sm w-100"
                :disabled="loadingMore"
                @click="loadMore"
              >
                {{ loadingMore ? $t('button.loading') : $t('button.loadMore') }}
              </button>
            </div>
          </div>
        </b-form-group>
      </div>

      <!-- Type of Bottle Section -->
      <!-- <validation-provider
        v-slot="{ errors }"
        :name="$t('field.bottleType')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.typeOfBottle')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <b-form-radio-group
            v-model="formData.bottleSize"
            :options="[
              { text: $t('field.bottledWaterLarge'), value: 3},
              { text: $t('field.bottledWaterSmall'), value: 1 }
            ]"
            :state="errors.length === 0 ? null : false"
          />
          <div class="d-flex align-items-center mt-2">
            <b-form-spinbutton
              v-model="formData.qty"
              min="1"
              :state="errors.length === 0 ? null : false"
            />
          </div>
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider> -->

      <!-- Service Price (from parent) -->
      <div
        v-if="servicePrice && servicePrice.total != null"
        class="mt-1 mb-2"
      >
        <div :style="{ paddingBottom: 'calc(0.438rem + 1px)' }">
          <span>{{ $t('field.servicePrice') }}</span>
        </div>
        <div class="text-body font-weight-bold">
          {{ totalPrice }}
        </div>
      </div>

      <!-- Service Time Section -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.scheduleType')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.serviceTime')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <b-form-radio-group
            v-model="formData.scheduleType"
            :options="[
              { text: $t('field.immediately'), value: 1 },
              { text: $t('field.customTime'), value: 2 }
            ]"
            :state="errors.length === 0 ? null : false"
          />
          <n-date-time-picker
            v-if="formData.scheduleType === 2"
            v-model="formData.scheduleDate"
            :state="errors.length === 0 ? null : false"
            :disable-before-today="true"
            :disable-after-today="false"
            class="mt-2"
            style="width: 50%"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>
    </validation-observer>
  </div>
</template>

<script>
  import {
    BFormGroup,
    BFormRadioGroup,
    BFormTextarea,
    BFormSpinbutton,
    BFormInput
  } from 'bootstrap-vue';
  import { ValidationProvider, ValidationObserver } from 'vee-validate';
  import NDateTimePicker from '@/components/NDateTimePicker.vue';
  import RepositoryFactory from '@/repositories/RepositoryFactory';
  import NSingleSelect from '@/components/NSingleSelect.vue';
  import { formatNumber } from '@/@core/utils/utils';

  const ThirdPartyRepository = RepositoryFactory.get('thirdParty');

  export default {
    name: 'WaterDeliveryForm',

    components: {
      BFormGroup,
      BFormRadioGroup,
      BFormTextarea,
      BFormSpinbutton,
      BFormInput,
      ValidationProvider,
      ValidationObserver,
      NDateTimePicker,
      NSingleSelect
    },

    props: {
      value: {
        type: Object,
        default: () => ({})
      },
    servicePrice: {
      type: Object,
      default: null
    },
      fullAddressList: {
        type: Array,
        default: () => []
      },
      allowClipboard: {
        type: Boolean,
        default: false
      },
      projectId: {
        type: String,
        default: null
      }
    },

    data() {
      return {
        formatNumber,
        formData: {
          scheduleType: 1,
          scheduleDate: '',
          addressType: 0,
          address: null,
          subscriptionType: 0,
          subscriptionPlanId: null,
          bottleSize: 1,
          qty: 1,
          items: [],
          description: ''
        },
        addressMode: 'other',
        loadingProducts: false,
        products: [],
        selectedCounts: {},
        page: 1,
        limit: 5,
        hasMore: true,
        loadingMore: false,
        descriptionDebounceTimer: null
      };
    },

    computed: {
      totalPrice() {
        const total = this.servicePrice?.total
        if (total == null) return ''
        let price = `$${formatNumber(total)}`
        const type = this.servicePrice?.subscriptionType
        let suffix = ''
        if (type === 1) {
          suffix = ` / ${this.$t('general.time')} (${this.$t('general.chargePerServiceCall')})`
        } else if (type === 2) {
          suffix = ` / ${this.$t('general.unit')} (${this.$t('general.monthlyCharge')})`
        } else if (type === 3) {
          suffix = ` / m² (${this.$t('general.monthlyCharge')})`
        }
        return suffix ? price + suffix : price
      },
      addressOptions() {
        const list = Array.isArray(this.fullAddressList)
          ? this.fullAddressList
          : [];
        const options = list.map(item => ({
          text: this.getAddressLabel(item),
          value: item
        }));
        if (options.length > 0) {
          options.push({
            text: this.$t('field.other'),
            value: {
              isCustom: true,
              typeKey: 'other',
              typeLabel: this.$t('field.other'),
              addressName: null,
              customAddress: ''
            }
          });
        }
        return options;
      },
      rentalAddressOptions() {
        const list = Array.isArray(this.fullAddressList)
          ? this.fullAddressList
          : [];
        return list.map(item => ({
          text: this.getAddressLabel(item),
          value: item
        }));
      },
      hasAddressOptions() {
        return this.addressOptions.length > 0;
      },
      addressModeOptions() {
        return [
          { text: this.$t('general.rentalAddress'), value: 'rental' },
          { text: this.$t('general.otherAddress'), value: 'other' }
        ];
      },
      customAddress: {
        get() {
          if (
            this.formData.address &&
            (this.formData.address.isCustom ||
              this.formData.address.typeKey === 'other')
          ) {
            return this.formData.address.customAddress || '';
          }
          return '';
        },
        set(val) {
          this.formData.address = {
            isCustom: true,
            typeKey: 'other',
            typeLabel: this.$t('field.other'),
            addressName: null,
            customAddress: val,
            files: [],
          };
        }
      }
    },

    watch: {
      value: {
        handler(newValue) {
          if (
            newValue &&
            JSON.stringify(newValue) !== JSON.stringify(this.formData)
          ) {
            this.formData = {
              scheduleType: newValue.scheduleType ?? 1,
              scheduleDate: newValue.scheduleDate ?? '',
              addressType: newValue.addressType ?? 0,
              address: newValue.address ?? null,
              subscriptionType: newValue.subscriptionType ?? 0,
              subscriptionPlanId: newValue.subscriptionPlanId ?? null,
              items: Array.isArray(newValue.items) ? newValue.items : [],
              description: newValue.description ?? ''
            };
            this.addressMode =
              this.formData.address &&
              (this.formData.address.isCustom ||
                this.formData.address.typeKey === 'other')
                ? 'other'
                : this.hasAddressOptions
                ? 'rental'
                : 'other';
          }
        },
        immediate: true,
        deep: true
      },
      formData: {
        handler(newValue, oldValue) {
          if (JSON.stringify(newValue) !== JSON.stringify(this.value)) {
            // Check if only description changed
            const onlyDescriptionChanged = newValue.description !== (oldValue?.description ?? '')
              && JSON.stringify({ ...newValue, description: '' }) === JSON.stringify({ ...(oldValue || {}), description: '' })
            
            if (onlyDescriptionChanged) {
              // Debounce description changes
              if (this.descriptionDebounceTimer) clearTimeout(this.descriptionDebounceTimer)
              this.descriptionDebounceTimer = setTimeout(() => {
                this.$emit('input', { ...newValue })
              }, 400)
            } else {
              // Emit immediately for other changes
              this.$emit('input', { ...newValue })
            }
          }
        },
        deep: true
      },
      fullAddressList: {
        handler() {
          this.formData.address = null;
          this.addressMode = this.hasAddressOptions ? 'rental' : 'other';
        }
      },
      selectedCounts: {
        handler() {
          this.formData.items = this.buildSelectedItems();
        },
        deep: true
      },
      addressMode(val) {
        if (val === 'rental') {
          if (
            this.formData.address &&
            (this.formData.address.isCustom ||
              this.formData.address.typeKey === 'other')
          ) {
            this.formData.address = null;
          }
        } else if (val === 'other') {
          if (
            this.formData.address &&
            !(this.formData.address.isCustom ||
              this.formData.address.typeKey === 'other')
          ) {
            this.formData.address = null;
          }
        }
      },
    },

    methods: {
      onImageError(event) {
        const element = event && event.target
        if (element && element.src !== window.location.origin + '/bottle-of-water.png') {
          element.src = '/bottle-of-water.png'
        }
      },
      getAddressLabel(item) {
        if (!item) return ''
        const locale = this.getLocale()
        const parts = []
        
        // Handle localized typeLabel - add to front with dash
        let typeLabel = ''
        if (item.typeLabel) {
          if (typeof item.typeLabel === 'object') {
            typeLabel = item.typeLabel[locale] 
              || item.typeLabel.en 
              || item.typeLabel.zh 
              || item.typeLabel.km 
              || item.typeLabel.vi 
              || ''
          } else {
            typeLabel = item.typeLabel
          }
          if (typeLabel) parts.push(`${typeLabel}-`)
        }
        
        // Handle localized addressName from translation object
        const addressName = item.translation.addressName[locale] || 
          item.translation.addressName.en ||
          item.translation.addressName.zh ||
          item.translation.addressName.km ||
          item.translation.addressName.vi ||
          item.addressName ||
          ''
        
        if (addressName) parts.push(addressName)
        
        return parts.join('')
      },
      getProductName(product) {
        const locale = this.getLocale();
        return (
          product?.translations?.[locale] ||
          product?.translations?.en ||
          product?.translations?.vi ||
          product?.translations?.km ||
          product?.translations?.zh ||
          product?.name?.[locale] ||
          product?.name?.en ||
          product?.name?.vi ||
          product?.name?.km ||
          product?.name?.zh ||
          product?.name ||
          '-'
        );
      },
      getLocale() {
        const [locale] = this.$i18n.locale.split('-');
        return locale || 'en';
      },
      formatPrice(amount) {
        const num = Number(amount || 0);
        return `$${num.toFixed(2)}`;
      },
      getQty(product) {
        const key = product.id || product.code;
        return this.selectedCounts[key] || 0;
      },
      increment(product) {
        const key = product.id || product.code;
        const current = this.selectedCounts[key] || 0;
        this.$set(this.selectedCounts, key, current + 1);
      },
      decrement(product) {
        const key = product.id || product.code;
        const current = this.selectedCounts[key] || 0;
        const next = Math.max(0, current - 1);
        this.$set(this.selectedCounts, key, next);
      },
      setQty(product, value) {
        const key = product.id || product.code;
        const qty = Math.max(0, parseInt(value) || 0);
        this.$set(this.selectedCounts, key, qty);
      },
      buildSelectedItems() {
        const items = [];
        for (const product of this.products) {
          const key = product.id || product.code;
          const qty = this.selectedCounts[key] || 0;
          if (qty > 0) {
            items.push({
              productOID: product.code,
              qty,
              ...product
            });
          }
        }
        return items;
      },
      async fetchProducts() {
        this.page = 1;
        await this.fetchProductsPage(this.page);
      },
      async fetchProductsPage(page) {
        try {
          if (page === 1) this.loadingProducts = true;
          const response = await ThirdPartyRepository.productList({
            page,
            limit: this.limit,
            projectOID: this.projectId
          });
          const data = (response && response.data) || {};
          const list =
            data?.data?.list ||
            data?.data?.items ||
            data?.data ||
            data?.list ||
            [];
          const onlyWater = Array.isArray(list)
            ? list.filter(
                p =>
                  (p?.service_type_code || p?.serviceTypeCode) ===
                    'WATER_DELIVERY_ORDER' || true
              )
            : [];

          if (page === 1) {
            this.products = onlyWater;
          } else {
            const existingKeys = new Set(
              this.products.map(p => p.id || p.code)
            );
            const merged = [...this.products];
            for (const p of onlyWater) {
              const key = p.id || p.code;
              if (!existingKeys.has(key)) merged.push(p);
            }
            this.products = merged;
          }

          this.hasMore = onlyWater.length >= this.limit;
        } catch (e) {
          if (page === 1) this.products = [];
          this.hasMore = false;
        } finally {
          if (page === 1) this.loadingProducts = false;
        }
      },
      async loadMore() {
        if (!this.hasMore || this.loadingMore) return;
        try {
          this.loadingMore = true;
          this.page += 1;
          await this.fetchProductsPage(this.page);
        } finally {
          this.loadingMore = false;
        }
      },
      async validate() {
        if (this.$refs.formValidation) {
          return this.$refs.formValidation.validate();
        }
        return false;
      },

      reset() {
        this.formData = {
          scheduleType: 1,
          scheduleDate: null,
          addressType: 0,
          address: null,
          subscriptionType: 0,
          subscriptionPlanId: null,
          bottleSize: 1,
          qty: 1,
          items: [],
          description: ''
        };
        if (this.$refs.formValidation) {
          this.$refs.formValidation.reset();
        }
      }
    },
    mounted() {
      this.fetchProducts();
    }
  };
</script>

<style lang="scss" scoped>
  .water-delivery-form {
    .form-group:last-child {
      margin-bottom: 0;
    }
  }

  .product-section {
    margin-top: 10px;
    margin-bottom: 20px;
  }

  .product-header {
    font-weight: 600;
    margin-bottom: 8px;
  }

  .product-list {
    display: grid;
    grid-auto-rows: minmax(64px, auto);
    gap: 10px;
    max-width: 50%;
  }

  .product-item {
    display: grid;
    grid-template-columns: 56px 1fr auto;
    gap: 10px;
    align-items: center;
  }

  .product-thumb {
    width: 56px;
    height: 56px;
    border-radius: 8px;
    overflow: hidden;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .product-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .product-title {
    font-weight: 500;
  }

  .product-price {
    color: #6c757d;
  }

  .qty-control {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .qty-btn {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid #8d5185;
    color: white;
    background: #8d5185;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .qty-btn[disabled] {
    cursor: not-allowed;
    background: white !important;
    color: #8d5185 !important;
  }

  .qty-value {
    min-width: 14px;
    text-align: center;
    font-weight: 600;
  }

  .qty-input {
    width: 100px;
    text-align: center;
    border-radius: 4px;
    padding: 4px;
    // font-weight: 600;
  }

  .btn-load-more {
    background: white !important;
    color: #8d5185 !important;
    border: 1px solid #8d5185 !important;
  }
</style>

<style>
  @media (min-width: 1200px) {
    .modal-xl {
      max-width: 72% !important;
    }
  }

  @media (min-width: 1200px) {
    .modal-xl {
      margin-left: 14%;
      margin-right: 14%;
    }
  }
</style>
